// Talks to the converter for the server tools (Phase 5). Browser-only.
//
// 1. POST /uploads      → where to send the file
// 2. send the file      → PUT to the converter (local), or a signed form POST
//                         straight to S3 (AWS) — with upload progress
// 3. POST /<job>        → the converted file comes straight back
// The converter deletes the file as soon as it has answered.
import { converterUrl } from "@/lib/converter";

export type ServerPhase = "starting" | "uploading" | "converting";

export type ServerProgress = {
  phase: ServerPhase;
  /** 0–1 while uploading. */
  fraction?: number;
};

export type ServerResult = {
  blob: Blob;
  /** e.g. "already-small" (Compress PDF). */
  note: string | null;
};

const OFFLINE =
  "We couldn't reach our converter. Check your internet connection and try again.";

async function errorMessage(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body.error === "string") return body.error;
  } catch {
    // not JSON
  }
  return res.status === 413
    ? "That file is too large for this tool."
    : "Something went wrong on our side. Please try again in a moment.";
}

/** fetch that retries once when the converter is waking up (cold start).
 *  Only for asking for an upload slot, which is safe to repeat. */
async function request(url: string, init: RequestInit): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await fetch(url, init);
      if ([502, 503, 504].includes(res.status) && attempt === 0) continue;
      return res;
    } catch {
      if (attempt > 0) throw new Error(OFFLINE);
    }
  }
}

type UploadSlot = {
  key: string;
  url: string;
  method: "PUT" | "POST";
  /** For POST: the signed form fields, sent before the file. */
  fields?: Record<string, string>;
};

function send(
  slot: UploadSlot,
  file: Blob,
  onFraction: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(slot.method, new URL(slot.url, `${converterUrl}/`).href);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onFraction(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(
            new Error(
              xhr.status === 413 || /EntityTooLarge/.test(xhr.responseText)
                ? "That file is too large for this tool."
                : "The upload didn't finish. Please try again.",
            ),
          );
    xhr.onerror = () => reject(new Error(OFFLINE));
    if (slot.method === "POST") {
      const form = new FormData();
      for (const [k, v] of Object.entries(slot.fields ?? {})) form.append(k, v);
      form.append("file", file); // S3 wants the file last
      xhr.send(form);
    } else {
      xhr.send(file);
    }
  });
}

/** Sends `file` to the converter's `job` (e.g. "compress") with `options`
 *  and returns the converted file. Errors are plain words for the page. */
export async function runOnServer(
  job: string,
  file: File,
  options: Record<string, unknown>,
  onProgress: (p: ServerProgress) => void = () => {},
): Promise<ServerResult> {
  if (!converterUrl) {
    throw new Error("Our converter isn't available on this copy of the site.");
  }
  onProgress({ phase: "starting" });
  const slotRes = await request(`${converterUrl}/uploads`, { method: "POST" });
  if (!slotRes.ok) throw new Error(await errorMessage(slotRes));
  const slot: UploadSlot = await slotRes.json();

  onProgress({ phase: "uploading", fraction: 0 });
  await send(slot, file, (fraction) =>
    onProgress({ phase: "uploading", fraction }),
  );

  onProgress({ phase: "converting" });
  // No retry here: the converter has already taken (and deleted) the file.
  const res = await fetch(`${converterUrl}/${job}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...options, key: slot.key }),
  }).catch(() => {
    throw new Error(OFFLINE);
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  return { blob: await res.blob(), note: res.headers.get("X-Result") };
}

/** Words for the progress bar. */
export function serverProgressLabel(p: ServerProgress): string {
  switch (p.phase) {
    case "starting":
      return "Starting the converter…";
    case "uploading":
      return `Sending your file… ${Math.round((p.fraction ?? 0) * 100)}%`;
    case "converting":
      return "Converting…";
  }
}
